'use client';

import React from 'react';

import { CinemaPartnerDto } from '@cineamo/api/models/cinema-partner/CinemaPartnerDto.types';
import { CinemaPartnerBenefitDto } from '@cineamo/api/models/cinema-partner-benefit/CinemaPartnerBenefitDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { getCinemaPartnerIcon } from '@/helpers/cinema-partner-helper';
import { getExternalLinkTarget } from '@/helpers/external-link-helper';
import { mapLinkDefault, mapLinkMobile } from '@/helpers/location-helper';
import { formatDistance } from '@cineamo/ui/src/helpers/location-helper';
import { getOs, OS } from '@cineamo/ui/src/helpers/os-helper';

import { IconExternalLink16 } from '@cineamo/ui/src/icons/ic-externalLink';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

const CinemaPartnerItem: React.FC<{
    domainConfiguration: DomainConfigurationDto;
    cinemaPartner: CinemaPartnerDto;
    cinemaPartnerBenefits: CinemaPartnerBenefitDto[];
}> = ({ domainConfiguration, cinemaPartner, cinemaPartnerBenefits }) => {
    const os = getOs();
    const externalLinkTarget = getExternalLinkTarget(domainConfiguration?.websiteConfiguration);

    return (
        <div className="w-full flex flex-row space-x-16 items-center py-[12px]">
            <div
                className="size-[48px] flex-none flex items-center justify-center rounded-full"
                style={{
                    backgroundColor: domainConfiguration.style.surfaceColor
                }}>
                {getCinemaPartnerIcon(cinemaPartner.type)}
            </div>
            <div className="flex flex-col flex-auto space-y-[8px]">
                <div className="flex flex-col space-y-[2px]">
                    <span className="text-body-bold-14">{cinemaPartner.name}</span>
                    <DotSeparatedLabel
                        className="w-full text-body-12 flex"
                        classNameLabel="flex flex-row items-center"
                        classNameDot="opacity-60"
                        labels={[
                            cinemaPartner.distanceToCinema && (
                                <span className="opacity-60 whitespace-nowrap">
                                    {formatDistance(cinemaPartner.distanceToCinema)}
                                </span>
                            ),
                            cinemaPartner.address && (
                                <a
                                    className="whitespace-wrap break-all line-clamp-1 opacity-60 cursor-pointer"
                                    href={
                                        os === OS.Other
                                            ? mapLinkDefault({
                                                  street: cinemaPartner.address
                                              })
                                            : mapLinkMobile({
                                                  street: cinemaPartner.address,
                                                  os: os
                                              })
                                    }>
                                    {cinemaPartner.address.split('\n').join(', ').replace(',,', ',')}
                                </a>
                            ),
                            cinemaPartner.link && (
                                <a
                                    href={cinemaPartner.link}
                                    target={externalLinkTarget.target}
                                    rel={externalLinkTarget.rel}>
                                    <IconExternalLink16
                                        className="clickable-element"
                                        style={{
                                            color: domainConfiguration.style.primaryColor
                                        }}
                                    />
                                </a>
                            )
                        ]}
                    />
                </div>
                <div className="flex flex-col">
                    {cinemaPartnerBenefits.map((cinemaPartnerBenefit) => (
                        <span
                            key={`CinemaPartnerBenefit#${cinemaPartnerBenefit.id}`}
                            className="text-body-12">
                            {cinemaPartnerBenefit.description}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CinemaPartnerItem;
